import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_BACKEND ||
  process.env.REACT_APP_API_URL ||
  "";

const normalizeApiBase = (base) => String(base || "").trim().replace(/\/+$/, "");

const DIRECT_IMAGE_EXTENSION_RE = /\.(?:png|jpe?g|gif|webp|avif|svg)(?:[?#].*)?$/i;
const STATIC_ASSET_PATH_RE = /^(?:\/)?(?:static\/|assets\/|asset-manifest\.json|favicon|logo|icon-)/i;
const HASHED_BUNDLE_ASSET_RE = /^[^/?#]+\.[a-f0-9]{8,}\.(?:png|jpe?g|gif|webp|avif|svg)(?:[?#].*)?$/i;

const getCurrentOrigin = () =>
  typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";

const normalizeStaticMediaPath = (raw) => {
  if (raw.startsWith("/static/media/")) return raw;
  if (raw.startsWith("static/media/")) return `/${raw}`;
  if (!raw.includes("/") && HASHED_BUNDLE_ASSET_RE.test(raw)) {
    return `/static/media/${raw}`;
  }
  return "";
};

const normalizeBackendStaticAssetCandidate = (raw, apiBase) => {
  const normalizedBase = normalizeApiBase(apiBase);
  if (!normalizedBase || !raw.startsWith(normalizedBase)) return "";
  const relativeToBackend = raw.slice(normalizedBase.length).replace(/^\/+/, "");
  return normalizeStaticMediaPath(relativeToBackend);
};

const normalizeDirectImageCandidate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(?:blob:|data:)/i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;

  const staticMediaPath = normalizeStaticMediaPath(raw);
  if (staticMediaPath) return staticMediaPath;

  if (raw.startsWith("/") && DIRECT_IMAGE_EXTENSION_RE.test(raw)) return raw;
  const isSafeStaticAssetPath =
    raw.startsWith("/") || raw.includes("/") || /^(?:asset-manifest\.json|favicon)/i.test(raw);
  if (STATIC_ASSET_PATH_RE.test(raw) && !raw.startsWith("api/") && isSafeStaticAssetPath) {
    return raw.startsWith("/") ? raw : `/${raw}`;
  }
  if (!raw.startsWith("/") && !raw.includes("/") && DIRECT_IMAGE_EXTENSION_RE.test(raw)) {
    return "";
  }

  try {
    const currentOrigin = getCurrentOrigin();
    if (!currentOrigin) return "";
    const url = new URL(raw, currentOrigin);
    if (url.origin !== currentOrigin) return "";
    const normalizedPath = normalizeStaticMediaPath(`${url.pathname}${url.search}`);
    if (normalizedPath) return normalizedPath;
    if (STATIC_ASSET_PATH_RE.test(url.pathname.replace(/^\/+/, ""))) {
      return `${url.pathname}${url.search}`;
    }
    if (DIRECT_IMAGE_EXTENSION_RE.test(`${url.pathname}${url.search}`)) {
      return `${url.pathname}${url.search}`;
    }
  } catch (_error) {
    return "";
  }

  return "";
};

export const isDirectImageCandidate = (value) => {
  return Boolean(normalizeDirectImageCandidate(value));
};

const absoluteCandidateUrl = (value, apiBase = DEFAULT_API_BASE_URL) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const backendStaticCandidate = normalizeBackendStaticAssetCandidate(raw, apiBase);
  if (backendStaticCandidate) return backendStaticCandidate;
  const directCandidate = normalizeDirectImageCandidate(raw);
  if (directCandidate) return directCandidate;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (!raw.startsWith("/api/") && !raw.startsWith("api/")) return "";
  const normalizedBase = normalizeApiBase(apiBase);
  const apiPath = raw.startsWith("/") ? raw : `/${raw}`;
  return `${normalizedBase}${apiPath}`;
};

export const toRelativeApiPath = (value, apiBase = DEFAULT_API_BASE_URL) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/api/")) return raw;
  const normalizedBase = normalizeApiBase(apiBase);
  if (normalizedBase && raw.startsWith(normalizedBase)) {
    const next = raw.slice(normalizedBase.length);
    return next.startsWith("/") ? next : `/${next}`;
  }
  try {
    const currentOrigin = getCurrentOrigin();
    const url = new URL(raw, currentOrigin || undefined);
    return url.pathname.startsWith("/api/") ? `${url.pathname}${url.search}` : "";
  } catch (_error) {
    return "";
  }
};

export const isProtectedImageApiPath = (path) =>
  /^\/api\/(?:advertisers|users)\/[^/]+\/logo\/file(?:\?|$)/i.test(String(path || "").trim());

const getAuthToken = () =>
  localStorage.getItem("clientAuthToken") ||
  localStorage.getItem("superadminToken") ||
  localStorage.getItem("authToken") ||
  "";

const protectedBlobUrlCache = new Map();

const shouldLogImageResolutionDiagnostics = () =>
  typeof process !== "undefined" && process.env.NODE_ENV !== "production";

const logImageResolutionDiagnostic = (event, details = {}) => {
  if (!shouldLogImageResolutionDiagnostics()) return;
  if (typeof console === "undefined" || typeof console.debug !== "function") return;
  console.debug("[useResolvedImageCandidates]", event, details);
};

const createImageResolutionAbortError = (message) => {
  if (typeof DOMException === "function") {
    return new DOMException(message, "AbortError");
  }
  const error = new Error(message);
  error.name = "AbortError";
  return error;
};

const normalizeProtectedImageBlob = (blob) =>
  !blob.type || blob.type === "application/octet-stream"
    ? new Blob([blob], { type: "image/png" })
    : blob;

const buildProtectedBlobPublicKey = (path, apiBase = DEFAULT_API_BASE_URL) =>
  `${normalizeApiBase(apiBase)}${path}`;

const buildProtectedBlobCacheKey = (path, apiBase = DEFAULT_API_BASE_URL) =>
  `${buildProtectedBlobPublicKey(path, apiBase)}::${getAuthToken()}`;

const acquireProtectedBlobUrl = (path, apiBase = DEFAULT_API_BASE_URL) => {
  const cacheKey = buildProtectedBlobCacheKey(path, apiBase);
  const publicCandidateKey = buildProtectedBlobPublicKey(path, apiBase);
  const cached = protectedBlobUrlCache.get(cacheKey);

  if (cached) {
    cached.refCount += 1;
    logImageResolutionDiagnostic("cache hit", {
      candidateKey: cached.publicCandidateKey,
      status: cached.status,
    });
    return {
      cacheKey,
      publicCandidateKey: cached.publicCandidateKey,
      promise:
        cached.status === "success"
          ? Promise.resolve(cached.objectUrl)
          : cached.promise,
    };
  }

  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const entry = {
    controller,
    objectUrl: "",
    promise: null,
    publicCandidateKey,
    refCount: 1,
    status: "pending",
  };

  protectedBlobUrlCache.set(cacheKey, entry);
  logImageResolutionDiagnostic("fetch start", { candidateKey: publicCandidateKey });

  entry.promise = fetchProtectedBlob(path, apiBase, { signal: controller?.signal })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(normalizeProtectedImageBlob(blob));
      const latestEntry = protectedBlobUrlCache.get(cacheKey);

      if (latestEntry !== entry || entry.refCount <= 0) {
        URL.revokeObjectURL(objectUrl);
        throw createImageResolutionAbortError("Protected media result is obsolete");
      }

      entry.objectUrl = objectUrl;
      entry.status = "success";
      logImageResolutionDiagnostic("fetch success", { candidateKey: publicCandidateKey });
      return objectUrl;
    })
    .catch((error) => {
      if (protectedBlobUrlCache.get(cacheKey) === entry) {
        protectedBlobUrlCache.delete(cacheKey);
      }
      logImageResolutionDiagnostic(error?.name === "AbortError" ? "fetch abort" : "fetch error", {
        candidateKey: publicCandidateKey,
        message: error?.message || "unknown image fetch error",
      });
      throw error;
    });

  return {
    cacheKey,
    publicCandidateKey,
    promise: entry.promise,
  };
};

const releaseProtectedBlobUrl = (cacheKey, reason = "release") => {
  const entry = protectedBlobUrlCache.get(cacheKey);
  if (!entry) return;

  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount > 0) return;

  protectedBlobUrlCache.delete(cacheKey);

  if (entry.status === "pending") {
    logImageResolutionDiagnostic("fetch abort", {
      candidateKey: entry.publicCandidateKey,
      reason,
    });
    entry.controller?.abort();
    return;
  }

  if (entry.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
    logImageResolutionDiagnostic("blob revoked", {
      candidateKey: entry.publicCandidateKey,
      reason,
    });
  }
};

export const clearResolvedImageCandidateCacheForTests = () => {
  protectedBlobUrlCache.forEach((entry) => {
    if (entry.status === "pending") {
      entry.controller?.abort();
      return;
    }
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
  });
  protectedBlobUrlCache.clear();
};

export async function fetchProtectedBlob(path, apiBase = DEFAULT_API_BASE_URL, { signal } = {}) {
  const normalizedBase = normalizeApiBase(apiBase);
  const token = getAuthToken();
  const res = await fetch(`${normalizedBase}${path}`, {
    method: "GET",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    signal,
  });

  if (!res.ok) {
    throw new Error(`Protected media request failed (${res.status})`);
  }

  return res.blob();
}

export function normalizeCandidateList(candidates, apiBase) {
  const seen = new Set();
  return (Array.isArray(candidates) ? candidates : [])
    .map((candidate) => absoluteCandidateUrl(candidate, apiBase))
    .filter(Boolean)
    .filter((candidate) => {
      if (seen.has(candidate)) return false;
      seen.add(candidate);
      return true;
    });
}

export default function useResolvedImageCandidates(
  candidates,
  { apiBase = DEFAULT_API_BASE_URL } = {}
) {
  const normalizedCandidates = useMemo(
    () => normalizeCandidateList(candidates, apiBase),
    [apiBase, candidates]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [resolvedSrc, setResolvedSrc] = useState("");
  const [phase, setPhase] = useState(normalizedCandidates.length ? "loading" : "idle");
  const normalizedCandidatesRef = useRef(normalizedCandidates);
  const failedCandidatesRef = useRef(new Set());
  const candidateSignature = useMemo(() => normalizedCandidates.join("\n"), [normalizedCandidates]);

  normalizedCandidatesRef.current = normalizedCandidates;

  useEffect(() => {
    failedCandidatesRef.current = new Set();
    setActiveIndex(0);
  }, [candidateSignature]);

  const advanceToNextCandidate = useCallback(() => {
    setActiveIndex((current) => {
      const currentCandidates = normalizedCandidatesRef.current;
      const currentCandidate = currentCandidates[current];
      if (currentCandidate) failedCandidatesRef.current.add(currentCandidate);

      for (let next = current + 1; next < currentCandidates.length; next += 1) {
        if (!failedCandidatesRef.current.has(currentCandidates[next])) return next;
      }

      return currentCandidates.length;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    let acquiredCacheKey = "";
    const currentCandidates = normalizedCandidatesRef.current;
    const setResolvedSrcIfChanged = (nextSrc) =>
      setResolvedSrc((currentSrc) => (currentSrc === nextSrc ? currentSrc : nextSrc));
    const setPhaseIfChanged = (nextPhase) =>
      setPhase((currentPhase) => (currentPhase === nextPhase ? currentPhase : nextPhase));

    if (!currentCandidates.length) {
      setResolvedSrcIfChanged("");
      setPhaseIfChanged("idle");
      return undefined;
    }

    if (activeIndex >= currentCandidates.length) {
      setResolvedSrcIfChanged("");
      setPhaseIfChanged("failed");
      return undefined;
    }

    const candidate = currentCandidates[activeIndex];
    const apiPath = toRelativeApiPath(candidate, apiBase);

    setResolvedSrcIfChanged("");
    setPhaseIfChanged("loading");

    if (!apiPath || !isProtectedImageApiPath(apiPath)) {
      logImageResolutionDiagnostic("final selected", { candidateKey: candidate, source: "direct" });
      setResolvedSrcIfChanged(candidate);
      setPhaseIfChanged("success");
      return undefined;
    }

    const acquired = acquireProtectedBlobUrl(apiPath, apiBase);
    acquiredCacheKey = acquired.cacheKey;

    acquired.promise
      .then((objectUrl) => {
        if (cancelled) return;
        logImageResolutionDiagnostic("final selected", {
          candidateKey: acquired.publicCandidateKey,
          source: "protected",
        });
        setResolvedSrcIfChanged(objectUrl);
        setPhaseIfChanged("success");
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.name === "AbortError") return;
        advanceToNextCandidate();
      });

    return () => {
      cancelled = true;
      if (acquiredCacheKey) releaseProtectedBlobUrl(acquiredCacheKey, "effect cleanup");
    };
  }, [activeIndex, advanceToNextCandidate, apiBase, candidateSignature]);

  const handleError = useCallback(() => {
    advanceToNextCandidate();
  }, [advanceToNextCandidate]);

  return {
    activeIndex,
    candidates: normalizedCandidates,
    exhausted: Boolean(normalizedCandidates.length) && activeIndex >= normalizedCandidates.length,
    handleError,
    phase,
    resolvedSrc,
  };
}
